import { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import type { DataTestId } from '@dynatrace/strato-components/core';
import { Flex, useBreakpoint } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import Breakpoints from '@dynatrace/strato-design-tokens/breakpoints-js';
import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { Label } from '../../../../forms/forms-core/components/label/Label.js';
import { SelectV2 } from '../../../../forms/selectv2/Select.js';
import { dataTableMessages } from '../../../DataTableV2/messages.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';

/**
 * DataTableV2 internal pagination component that is actually rendering the pagination.
 * @internal
 */
interface DataTableV2PaginationInternalProps extends DataTestId {
  /**
   * The element's unique identifier. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id).
   */
  id?: string;
  /** Whether the previous page is available (if not, the go to previous page button should be disabled).	*/
  enablePreviousPage?: boolean;
  /** Whether the next page is available (if not, the go to next page button should be disabled). */
  enableNextPage?: boolean;
  /** Number of rows per page  */
  pageSize: number;
  /** Handler that is called when the page size changes. */
  onPageSizeChange: (newPageSize: number) => void;

  /** Number that represents the number of available pages. Will be -1 for server-side pagination.  */
  pageCount: number;
  /** Current page index in a controlled scenario. */
  pageIndex: number;
  /** Called when the page index changes. Provides the changed page index along with the underlying trigger. */
  onPageIndexChange: (newPageIndex: number) => void;
  /** Defines the available numbers for the pageSize select. */
  pageSizeOptions: number[];

  /**
   * The total number of rows.
   * Will be used to determine the total number of pages to display the indicator "Page X of Y" for server-side pagination.
   */
  totalRowsCount?: number;
}

/**
 * Internal component that actually renders the pagination.
 * @internal
 */
export function DataTableV2PaginationInternal(
  props: DataTableV2PaginationInternalProps,
) {
  const table = useDataTableV2();
  const intl = useIntl();

  const pageSizeSelectId = useId();
  const pageIndexSelectId = useId();

  const isServerSidePaginated =
    props.enableNextPage !== undefined ||
    props.enablePreviousPage !== undefined;

  const totalPages =
    isServerSidePaginated && props.totalRowsCount !== undefined
      ? Math.ceil(props.totalRowsCount / props.pageSize)
      : props.pageCount;

  // An array of zero-based index integers corresponding to available pages in the table
  const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);

  const smallScreen = useBreakpoint(`(max-width: ${Breakpoints.Max.Mobile}px)`);

  // If there is only one entry in the pageSizeOptions, we do not render the select
  const hasPageSizeOptions = props.pageSizeOptions.length > 1;

  return (
    <Flex
      id={props.id}
      data-testid={props['data-testid']}
      justifyContent={hasPageSizeOptions ? 'space-between' : 'flex-end'}
      paddingTop={8}
      role="navigation"
      flexWrap="wrap"
    >
      {hasPageSizeOptions && (
        <Flex alignItems="center">
          <SelectV2
            id={pageSizeSelectId}
            value={props.pageSize}
            required
            onChange={(newPageSize) =>
              newPageSize !== null && props.onPageSizeChange?.(newPageSize)
            }
            aria-label={intl.formatMessage(dataTableMessages.selectPageSize)}
          >
            <SelectV2.Content>
              {props.pageSizeOptions.map((pageSize) => (
                <SelectV2.Option
                  key={`${pageSize}`}
                  id={pageSize}
                  value={pageSize}
                >
                  {`${pageSize}`}
                </SelectV2.Option>
              ))}
            </SelectV2.Content>
          </SelectV2>
          <Label htmlFor={pageSizeSelectId}>
            <Text as="span" style={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage
                description="Text to display when choosing the number of rows per page."
                defaultMessage="rows per page"
                id="lu2hmVHtfcnVRPtz"
              />
            </Text>
          </Label>
        </Flex>
      )}

      <Flex gap={16} justifySelf="flex-end">
        {(!isServerSidePaginated ||
          (isServerSidePaginated && totalPages !== -1)) && (
          <Flex flexWrap="nowrap" alignItems="center">
            <Label htmlFor={pageIndexSelectId}>
              <Text as="p">
                <FormattedMessage
                  description="Label for page selection dropdown."
                  defaultMessage="Page"
                  id="LCNiBr0aBntMq391"
                />
              </Text>
            </Label>
            <SelectV2
              id={pageIndexSelectId}
              value={props.pageIndex}
              onChange={(newPageIndex) =>
                newPageIndex !== null && props.onPageIndexChange(newPageIndex)
              }
              aria-label={intl.formatMessage(dataTableMessages.selectPage)}
            >
              <SelectV2.Content>
                {pageCountArray.length === 0 ? (
                  <SelectV2.Option key={'pageIndex-0'} id={0} value={0}>
                    {0}
                  </SelectV2.Option>
                ) : (
                  pageCountArray.map((pageIndex) => (
                    <SelectV2.Option
                      key={`pageIndex-${pageIndex}`}
                      id={pageIndex}
                      value={pageIndex}
                    >
                      {pageIndex + 1}
                    </SelectV2.Option>
                  ))
                )}
              </SelectV2.Content>
            </SelectV2>
            {!smallScreen && (
              <Label htmlFor={pageIndexSelectId}>
                <Text
                  as="span"
                  textStyle="base"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FormattedMessage
                    description="Text displaying the amount of pages."
                    defaultMessage="of {totalPages}"
                    id="HzhWa5UT4EGJIstf"
                    values={{ totalPages }}
                  />
                </Text>
              </Label>
            )}
          </Flex>
        )}
        {(!smallScreen || isServerSidePaginated) && (
          <Flex gap={2}>
            <Button
              onClick={() => {
                const nextPageIndex = Math.max(0, props.pageIndex - 1);
                props.onPageIndexChange(nextPageIndex);
              }}
              disabled={
                table.options.manualPagination
                  ? !table.getState().pagination.enablePreviousPage
                  : !table.getCanPreviousPage()
              }
              aria-label={intl.formatMessage({
                defaultMessage: 'Go to previous page.',
                id: 'VoqQWp9yEM0QM8eF',
                description: 'Aria label to add to the `previous` button',
              })}
              variant="default"
            >
              <Button.Prefix>
                <ChevronLeftSmallIcon />
              </Button.Prefix>
            </Button>
            <Button
              onClick={() => {
                props.onPageIndexChange(props.pageIndex + 1);
              }}
              disabled={
                table.options.manualPagination
                  ? !table.getState().pagination.enableNextPage
                  : !table.getCanNextPage()
              }
              aria-label={intl.formatMessage({
                defaultMessage: 'Go to next page.',
                id: 'KAAzM9PVtiJno9ks',
                description: 'Aria label to add to the `next` button',
              })}
              variant="default"
            >
              <Button.Prefix>
                <ChevronRightSmallIcon />
              </Button.Prefix>
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
